/**
 * All metric CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.metric;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_METRIC = "INVALIDATE_SELECTED_METRIC"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_METRIC
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleMetricById(id))
  } else {
    return dispatch(returnSingleMetricPromise(id)); // return promise that contains metric
  }
}

export const returnSingleMetricPromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve) => {
    resolve({
      id: id
      , item: getState().metric.byId[id]
      , success: true
      , type: "RETURN_SINGLE_METRIC_WITHOUT_FETCHING"
    })
  });
}

export const REQUEST_SINGLE_METRIC = "REQUEST_SINGLE_METRIC";
function requestSingleMetric(id) {
  return {
    id
    , type: REQUEST_SINGLE_METRIC
  }
}

export const RECEIVE_SINGLE_METRIC = "RECEIVE_SINGLE_METRIC";
function receiveSingleMetric(json) {
  return {
    error: json.message
    , id: json.metric ? json.metric._id : null
    , item: json.metric
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_SINGLE_METRIC
  }
}

export function fetchSingleMetricById(metricId) {
  return dispatch => {
    dispatch(requestSingleMetric(metricId))
    return callAPI(`/api/metrics/${metricId}`)
      .then(json => dispatch(receiveSingleMetric(json)))
  }
}

export const ADD_SINGLE_METRIC_TO_MAP = "ADD_SINGLE_METRIC_TO_MAP";
export function addSingleMetricToMap(item) {
  return {
    item
    , type: ADD_SINGLE_METRIC_TO_MAP
  }
}

export const REQUEST_CREATE_METRIC = "REQUEST_CREATE_METRIC";
function requestCreateMetric(metric) {
  return {
    metric
    , type: REQUEST_CREATE_METRIC
  }
}

export const RECEIVE_CREATE_METRIC = "RECEIVE_CREATE_METRIC";
function receiveCreateMetric(json) {
  return {
    error: json.message
    , id: json.metric ? json.metric._id : null
    , item: json.metric
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_CREATE_METRIC
  }
}

export function sendCreateMetric(data) {
  return dispatch => {
    dispatch(requestCreateMetric(data))
    return callAPI('/api/metrics', 'POST', data)
      .then(json => dispatch(receiveCreateMetric(json)))
  }
}

export const REQUEST_UPDATE_METRIC = "REQUEST_UPDATE_METRIC";
function requestUpdateMetric(metric) {
  return {
    type: REQUEST_UPDATE_METRIC
    , metric
  }
}

export const RECEIVE_UPDATE_METRIC = "RECEIVE_UPDATE_METRIC";
function receiveUpdateMetric(json) {
  return {
    error: json.message
    , item: json.metric
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_UPDATE_METRIC
  }
}

export function sendUpdateMetric(data) {
  return dispatch => {
    dispatch(requestUpdateMetric(data))
    return callAPI(`/api/metrics/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateMetric(json)))
  }
}

export const REQUEST_DELETE_METRIC = "REQUEST_DELETE_METRIC";
function requestDeleteMetric(metricId) {
  return {
    metricId
    , type: REQUEST_DELETE_METRIC
  }
}

export const RECEIVE_DELETE_METRIC = "RECEIVE_DELETE_METRIC";
function receiveDeleteMetric(json) {
  return {
    error: json.message
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_DELETE_METRIC
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteMetric(id))
    return callAPI(`/api/metrics/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteMetric(json)))
  }
}


/**
 * METRIC LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest metricLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.metric.lists, {});
  let list = { ...state.metric.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnMetricListPromise(...listArgs));
  }
}

export const returnMetricListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.metric.byId[id]);

  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_METRIC_LIST_WITHOUT_FETCHING"
      , listArgs: listArgs
      , list: listItems
      , success: true
    })
  });
}

export const REQUEST_METRIC_LIST = "REQUEST_METRIC_LIST"
function requestMetricList(listArgs) {
  return {
    listArgs
    , type: REQUEST_METRIC_LIST
  }
}

export const RECEIVE_METRIC_LIST = "RECEIVE_METRIC_LIST"
function receiveMetricList(json, listArgs) {
  return {
    error: json.message
    , filter: json.filter
    , list: json.metrics
    , listArgs
    , pagination: json.pagination
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_METRIC_LIST
  }
}

export const ADD_METRIC_TO_LIST = "ADD_METRIC_TO_LIST";
export function addMetricToList(id, ...listArgs) {
  // console.log("Add metric to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_METRIC_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_METRIC_FROM_LIST = "REMOVE_METRIC_FROM_LIST"
export function removeMetricFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_METRIC_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    dispatch(requestMetricList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/metrics/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/metrics/by-[ARG1]/[ARG2]".
     *    ex: /api/metrics/by-category/:category
     *  if 2nd arg is an array, though, return "/api/metrics/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/metrics";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && listArgs[0] === "search") {
      apiTarget += "/search" + listArgs[1];
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of metrics given an array of metric id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveMetricList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_METRIC_FILTER = "SET_METRIC_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    filter
    , listArgs
    , type: SET_METRIC_FILTER
  }
}

export const SET_METRIC_PAGINATION = "SET_METRIC_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    listArgs
    , pagination
    , type: SET_METRIC_PAGINATION
  }
}

export const INVALIDATE_METRIC_LIST = "INVALIDATE_METRIC_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    listArgs
    , type: INVALIDATE_METRIC_LIST
  }
}



// WEBPACK FOOTER //
// modules/metric/metricActions.js